// Stundenzettel für die Lohnbuchhaltung.
//
// ══ Was DATEV wirklich braucht ══
//
// Es gibt kein einzelnes "DATEV-Format" für Arbeitszeiten. Was der Steuerberater
// bekommt, ist in aller Regel eine Aufstellung je Mitarbeiter und Monat, die er
// in "Lohn und Gehalt" einliest oder abtippt: Personalnummer, Name, Tag, Kommen,
// Gehen, Pause, Stunden.
//
// Deshalb wird hier eine CSV erzeugt, die
//   * Semikolon trennt (das erwartet Excel im deutschen Gebietsschema),
//   * Komma als Dezimaltrennzeichen benutzt (8,25 Stunden, nicht 8.25),
//   * eine BOM voranstellt — sonst zeigt Excel "Straße" als "StraÃŸe",
//   * STORNIERTE Einträge mit ausweist statt sie zu verschweigen.
//
// Der letzte Punkt ist der wichtige: Eine Aufzeichnung, aus der Korrekturen
// spurlos verschwinden, ist für die Prüfung wertlos. Was storniert wurde, bleibt
// sichtbar — mit dem Grund daneben.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface LohnZeile {
  personalnummer: string;
  name: string;
  datum: string;
  kommen: string;
  gehen: string;
  pause_min: number;
  stunden: number;
  quelle: string;
  storniert: boolean;
  storno_grund: string | null;
  geaendert: boolean;
}

/** Alle Zeiten eines Monats, aufbereitet für den Lohn. */
export function useLohnMonat(jahr: number, monat: number) {
  return useQuery({
    queryKey: ["lohn-monat", jahr, monat],
    queryFn: async (): Promise<LohnZeile[]> => {
      const von = new Date(Date.UTC(jahr, monat - 1, 1)).toISOString();
      const bis = new Date(Date.UTC(jahr, monat, 1)).toISOString();

      const { data, error } = await supabase
        .from("time_entries")
        .select(
          `id, profile_id, started_at, ended_at, break_minutes, quelle,
           storniert_am, storno_grund,
           person:profiles!time_entries_profile_id_fkey(full_name, personalnummer, im_lohn)`
        )
        .gte("started_at", von)
        .lt("started_at", bis)
        .order("started_at");
      if (error) throw error;

      const eintraege = (data ?? []) as unknown as {
        id: string;
        /** Wem gehört der Eintrag? Entscheidet, ob eine Änderung eine Korrektur ist. */
        profile_id: string;
        started_at: string;
        ended_at: string | null;
        break_minutes: number;
        quelle: string;
        storniert_am: string | null;
        storno_grund: string | null;
        person: { full_name: string | null; personalnummer: string | null; im_lohn: boolean } | null;
      }[];

      // Entwickler und Testkonten haben zwar einen Zugang, stehen aber nicht auf
      // der Lohnliste — ihre Zeiten gehören nicht in den Stundenzettel.
      const eintraege2 = eintraege.filter((e) => e.person?.im_lohn !== false);

      // Welche Einträge wurden nachträglich angefasst? Das muss auf dem Zettel
      // stehen — der Steuerberater soll sehen, wo jemand korrigiert hat.
      // ⚠ NICHT jede Protokollzeile ist eine Korrektur.
      //
      // Der Trigger schreibt jede Änderung mit — auch das Ausstempeln, denn der
      // Eintrag beginnt ohne Ende: Feierabend setzt `ended_at`, der automatische
      // Pausenabzug `break_minutes`. Damit war JEDER abgeschlossene Tag als
      // „korrigiert" markiert, und Fabians drei sauberen Tage sahen aus, als
      // hätte jemand daran herumgedreht. Eine Auffälligkeit, die immer leuchtet,
      // übersieht man genau dann, wenn sie mal echt ist.
      //
      // Als Korrektur gilt deshalb nur:
      //   • eine Änderung durch jemand ANDEREN als den Mitarbeiter selbst
      //     (die Leitung hat nachgebessert), oder
      //   • eine Änderung an einem anderen Feld als dem Feierabend-Paar
      //     (started_at etwa lässt sich beim Stempeln gar nicht ändern).
      const ids = eintraege2.map((e) => e.id);
      const wemGehoert = new Map(eintraege2.map((e) => [e.id, e.profile_id]));
      const geaendert = new Set<string>();
      if (ids.length) {
        const { data: log } = await supabase
          .from("time_entry_log")
          .select("entry_id, aktion, feld, geaendert_von")
          .in("entry_id", ids)
          .neq("aktion", "angelegt");

        const abschluss = new Set(["ended_at", "break_minutes"]);
        for (const l of (log ?? []) as {
          entry_id: string;
          feld: string | null;
          geaendert_von: string | null;
        }[]) {
          const selbst = l.geaendert_von && l.geaendert_von === wemGehoert.get(l.entry_id);
          if (selbst && l.feld && abschluss.has(l.feld)) continue; // normales Ausstempeln
          geaendert.add(l.entry_id);
        }
      }

      // Ein nachgetragener oder korrigierter Eintrag ist auffällig, auch wenn
      // ihn danach niemand mehr angefasst hat: Er wurde nicht gestempelt,
      // sondern eingetragen. Genau das will der Steuerberater sehen.
      for (const e of eintraege2) {
        if (e.quelle === "korrektur" || e.quelle === "nachtrag") geaendert.add(e.id);
      }

      return eintraege2.map((e) => {
        const ende = e.ended_at ? new Date(e.ended_at) : null;
        const start = new Date(e.started_at);

        const brutto = ende ? (ende.getTime() - start.getTime()) / 60000 : 0;
        const netto = Math.max(0, brutto - e.break_minutes);

        return {
          personalnummer: e.person?.personalnummer ?? "",
          name: e.person?.full_name ?? "Ohne Namen",
          datum: start.toLocaleDateString("de-DE"),
          kommen: uhr(start),
          gehen: ende ? uhr(ende) : "",
          pause_min: e.break_minutes,
          // Auf zwei Stellen — der Lohn rechnet in Industriestunden (8,25 = 8 h 15 min).
          stunden: Math.round((netto / 60) * 100) / 100,
          quelle: e.quelle,
          storniert: !!e.storniert_am,
          storno_grund: e.storno_grund,
          geaendert: geaendert.has(e.id),
        };
      });
    },
  });
}

const uhr = (d: Date) =>
  d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

/** CSV bauen — Semikolon, Komma-Dezimal, BOM. Alles, was Excel hier erwartet. */
export function baueCsv(zeilen: LohnZeile[], jahr: number, monat: number): string {
  const kopf = [
    "Personalnummer",
    "Name",
    "Datum",
    "Kommen",
    "Gehen",
    "Pause (min)",
    "Stunden",
    "Erfassung",
    "Storniert",
    "Grund",
    "Nachträglich geändert",
  ];

  const QUELLE: Record<string, string> = {
    stempeluhr: "gestempelt",
    nachtrag: "nachgetragen",
    korrektur: "korrigiert",
  };

  const zeile = (z: LohnZeile) =>
    [
      z.personalnummer,
      z.name,
      z.datum,
      z.kommen,
      z.gehen,
      String(z.pause_min),
      // Deutsche Dezimalschreibweise — sonst liest Excel 8.25 als Datum.
      z.stunden.toFixed(2).replace(".", ","),
      QUELLE[z.quelle] ?? z.quelle,
      z.storniert ? "ja" : "",
      z.storno_grund ?? "",
      z.geaendert ? "ja" : "",
    ]
      .map(csvFeld)
      .join(";");

  // Storniertes zählt nicht mit — steht aber trotzdem in der Liste.
  const summe = zeilen.filter((z) => !z.storniert).reduce((s, z) => s + z.stunden, 0);

  return (
    "﻿" + // BOM: ohne sie macht Excel aus "Straße" ein "StraÃŸe"
    [
      `Stundenzettel ${String(monat).padStart(2, "0")}/${jahr}`,
      "",
      kopf.map(csvFeld).join(";"),
      ...zeilen.map(zeile),
      "",
      `;;;;;;Summe;${summe.toFixed(2).replace(".", ",")}`,
    ].join("\r\n")
  );
}

/** Semikolon, Anführungszeichen und Zeilenumbrüche müssen maskiert werden. */
function csvFeld(w: string): string {
  const s = String(w ?? "");
  return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function ladeCsvHerunter(csv: string, dateiname: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = dateiname;
  a.click();
  URL.revokeObjectURL(url);
}
